import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useNeura } from '../../contexts/NeuraContext.tsx';
import { motion, AnimatePresence } from 'framer-motion';
import { NeuraAvatar } from './NeuraAvatar.tsx';
import useSound from 'use-sound';
import { QuantumGlyphs } from './QuantumGlyphs.tsx';


const ThoughtContainer = styled(motion.div)`
  position: relative;
  max-width: 1200px;
  margin: 2rem auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  align-items: stretch;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputSection = styled.div`
  position: relative;
`;

const NeuralField = styled(motion.input)`
  width: 90%;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  color: #fff;
  font-size: 1.2rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.3);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
  }
`;

const NeuralActivity = styled(motion.div)<{ activity: string }>`
  position: absolute;
  
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ activity }) => 
    activity === 'processing' ? '#00ff88' :
    activity === 'engaged' ? '#88ff00' :
    '#444'};
`;

const ResponseContainer = styled(motion.div)`
  position: relative;
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  z-index: 5;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 15px;
  border: 1px solid rgba(0, 255, 136, 0.2);
  backdrop-filter: blur(10px);
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 15px;
    background: radial-gradient(
      circle at center,
      rgba(0, 255, 136, 0.05) 0%,
      transparent 70%
    );
    pointer-events: none;
  }
`;

const AvatarWrapper = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const FloatingAvatar = styled(motion.div)`
  cursor: pointer;
  position: relative;
  z-index: 1;
`;

const ResponseContent = styled.div`
  flex: 1;
  position: relative;
  z-index: 1;
`;

const ResponseText = styled.div`
  color: #fff;
  font-size: 1.1rem;
  line-height: 1.5;
  white-space: pre-wrap;
  min-height: 1.5rem;
  max-height: 300px;
  overflow-y: auto;
  scroll-behavior: smooth;
  
  p {
    margin: 0.5rem 0;
  }
  
  p + p {
    margin-top: 0.75rem;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 255, 136, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 255, 136, 0.3);
    border-radius: 3px;
  }
`;

const StreamingText = styled(motion.div)`
  color: #00ffc8;
  font-family: 'Courier New', monospace;
  position: relative;
  
  &::after {
    display: none;
  }
`;

const StreamContainer = styled(motion.div)`
  position: relative;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
      transparent,
      rgba(0, 255, 200, 0.1),
      transparent
    );
    animation: stream 2s linear infinite;
  }

  @keyframes stream {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const cleanResponse = (text: string) => {
  return text
    .replace(/\[INSIGHT\]\s*/g, '💡 ')
    .replace(/\[GLYPHS\].*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s+/g, ' ')
    .trim();
};

const StreamingResponse: React.FC<{ text: string }> = ({ text }) => {
  const responseRef = useRef<HTMLDivElement>(null);
  const cleanedText = cleanResponse(text);

  useEffect(() => {
    if (responseRef.current) {
      responseRef.current.scrollTop = responseRef.current.scrollHeight;
    }
  }, [cleanedText]);

  return (
    <ResponseText ref={responseRef}>
      {cleanedText}
    </ResponseText>
  );
};

const ErrorMessage = styled(motion.div)`
  color: #ff4444;
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  backdrop-filter: blur(5px);
`;

const QuantumField = styled(motion.div)`
  width: 60px;
  height: 15px;
  margin-top: 10px;
  position: relative;
  background: rgba(0, 255, 136, 0.05);
  border-radius: 10px;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(0, 255, 136, 0.2),
      transparent
    );
  }
`;

const EnergyWave = styled(motion.div)`
  position: absolute;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-evenly;
  align-items: center;
`;

const EnergyParticle = styled(motion.div)`
  width: 2px;
  height: 2px;
  background: #00ff88;
  border-radius: 50%;
  box-shadow: 0 0 4px #00ff88;
`;

const QuantumOscilloscope = styled(motion.div)`
  width: 80px;
  height: 20px;
  margin-top: 10px;
  position: relative;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 4px;
  overflow: hidden;
`;

const WaveCanvas = styled(motion.canvas)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const QuantumFace = styled(motion.div)`
  width: 120px;
  height: 140px;
  margin-top: 10px;
  position: relative;
  background: linear-gradient(
    180deg,
    rgba(0, 255, 136, 0.05) 0%,
    rgba(0, 255, 136, 0.1) 100%
  );
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 60px 60px 40px 40px;
  overflow: hidden;
  box-shadow: 
    0 0 20px rgba(0, 255, 136, 0.1),
    inset 0 0 30px rgba(0, 255, 136, 0.05);
`;

const QuantumEye = styled(motion.div)`
  width: 18px;
  height: 8px;
  border: 1px solid rgba(0, 255, 136, 0.6);
  border-radius: 20px;
  position: absolute;
  background: rgba(0, 255, 136, 0.1);
  box-shadow: 
    0 0 10px rgba(0, 255, 136, 0.3),
    inset 0 0 8px rgba(0, 255, 136, 0.2);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    width: 4px;
    height: 4px;
    background: #00ff88;
    border-radius: 50%;
    top: 2px;
    left: 7px;
    box-shadow: 0 0 6px #00ff88;
  }
`;

const QuantumMouth = styled(motion.div)`
  width: 30px;
  height: 12px;
  position: absolute;
  left: 50%;
  bottom: 35px;
  transform: translateX(-50%);
  border: 1px solid rgba(0, 255, 136, 0.6);
  border-radius: 20px;
  background: rgba(0, 255, 136, 0.1);
  box-shadow: 
    0 0 10px rgba(0, 255, 136, 0.2),
    inset 0 0 8px rgba(0, 255, 136, 0.1);
  overflow: hidden;
`;

const MouthWave = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  gap: 2px;
`;

const WaveLine = styled(motion.div)`
  width: 2px;
  height: 2px;
  background: #00ff88;
  border-radius: 50%;
`;

const QuantumEar = styled(motion.div)`
  width: 4px;
  height: 25px;
  position: absolute;
  background: rgba(0, 255, 136, 0.2);
  border: 1px solid rgba(0, 255, 136, 0.4);
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 255, 136, 0.2);

  &::before {
    content: '';
    position: absolute;
    top: 5px;
    width: 8px;
    height: 8px;
    border: 1px solid rgba(0, 255, 136, 0.4);
    border-radius: 50%;
    background: rgba(0, 255, 136, 0.1);
  }
`;

const SpeechVisualizer: React.FC<{ isSpeaking: boolean; isWinking?: boolean }> = ({ 
  isSpeaking,
  isWinking 
}) => {
  const [isEarTwitching, setIsEarTwitching] = useState(false);
  const [twitchSide, setTwitchSide] = useState<'left' | 'right' | 'both'>('both');

  // Random ear movements
  useEffect(() => {
    const earTwitchInterval = setInterval(() => {
      if (!isSpeaking && Math.random() < 0.3) { // 30% chance to twitch when not speaking
        setIsEarTwitching(true);
        setTwitchSide(Math.random() < 0.3 ? 'both' : Math.random() < 0.5 ? 'left' : 'right');
        setTimeout(() => setIsEarTwitching(false), 500);
      }
    }, 3000); // Check every 3 seconds

    return () => clearInterval(earTwitchInterval);
  }, [isSpeaking]);

  return (
    <QuantumFace>
      <QuantumEye
        style={{ top: '45px', left: '30px' }}
        animate={isWinking ? {
          scaleY: [1, 0.1, 1],
          transition: { duration: 0.4 }
        } : isSpeaking ? {
          scaleY: [1, 1.1, 1],
          opacity: [0.8, 1, 0.8],
          filter: ['brightness(1)', 'brightness(1.2)', 'brightness(1)'],
          transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
        } : {}}
      />
      
      <QuantumEye
        style={{ top: '45px', right: '30px' }}
        animate={isSpeaking ? {
          scaleY: [1, 1.1, 1],
          opacity: [0.8, 1, 0.8],
          filter: ['brightness(1)', 'brightness(1.2)', 'brightness(1)'],
          transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.75 }
        } : {}}
      />
      
      <QuantumEar
        style={{ left: -2, top: '50px' }}
        animate={isEarTwitching && (twitchSide === 'left' || twitchSide === 'both') ? {
          rotate: [-5, 5, -5, 0],
          scaleY: [1, 1.1, 1],
          transition: { duration: 0.5 }
        } : isSpeaking ? {
          scaleX: [1, 1.2, 1],
          opacity: [0.8, 1, 0.8],
          filter: ['brightness(1)', 'brightness(1.1)', 'brightness(1)'],
          transition: { duration: 1.2, repeat: Infinity }
        } : {}}
      />
      
      <QuantumEar
        style={{ right: -2, top: '50px' }}
        animate={isEarTwitching && (twitchSide === 'right' || twitchSide === 'both') ? {
          rotate: [5, -5, 5, 0],
          scaleY: [1, 1.1, 1],
          transition: { duration: 0.5 }
        } : isSpeaking ? {
          scaleX: [1, 1.2, 1],
          opacity: [0.8, 1, 0.8],
          filter: ['brightness(1)', 'brightness(1.1)', 'brightness(1)'],
          transition: { duration: 1.2, repeat: Infinity, delay: 0.6 }
        } : {}}
      />
      
      <QuantumMouth>
        <MouthWave>
          {[...Array(5)].map((_, i) => (
            <WaveLine
              key={i}
              animate={isSpeaking ? {
                height: [2, 6, 2],
                opacity: [0.5, 1, 0.5],
                transition: {
                  duration: 0.4,
                  repeat: Infinity,
                  delay: i * 0.1
                }
              } : {
                height: 2,
                opacity: 0.5
              }}
            />
          ))}
        </MouthWave>
      </QuantumMouth>
    </QuantumFace>
  );
};

const BottomControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 255, 136, 0.2);
`;

const StopButton = styled(motion.button)`
  background: rgba(255, 68, 68, 0.2);
  border: 1px solid rgba(255, 68, 68, 0.4);
  color: #ff4444;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: rgba(255, 68, 68, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ClickableAvatarWrapper = styled(motion.div)<{ isSpeaking?: boolean }>`
  position: relative;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    background: ${props => props.isSpeaking ? 
      'radial-gradient(circle, rgba(0,255,136,0.1) 0%, transparent 70%)' : 
      'none'};
    pointer-events: none;
  }
`;

const PulsingRing = styled(motion.div)`
  position: absolute;
  inset: -8px;
  border-radius: 50%;
  border: 2px solid rgba(0, 255, 136, 0.2);
  pointer-events: none;
`;

export const ThoughtInput: React.FC = () => {
  const { 
    neuralActivity, 
    streamingResponse,
    isActuallySpeaking,
    isMuted,
    toggleMute,
    stopSpeaking,
    processThought,
    isPlaying,
    playFromBeginning,
    pauseSpeech,
    resumeSpeech,
    currentVoice,
    setCurrentVoice,
    voiceOptions,
    isSpeaking,
    replayResponse,
  } = useNeura();
  const [thought, setThought] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBooping, setIsBooping] = useState(false);
  const [playBeep] = useSound('/sounds/beep.mp3', { volume: 0.3 });
  const [playError] = useSound('/sounds/error.mp3', { volume: 0.3 });
  const [isWinking, setIsWinking] = useState(false);
  const [currentGlyphs, setCurrentGlyphs] = useState<string[]>([]);
  const [isResponseComplete, setIsResponseComplete] = useState(false);
  
  const randomMotion = {
    x: [0, -5, 5, 0],
    y: [0, 5, -5, 0],
    rotate: [0, -5, 5, 0],
    transition: {
      duration: 10,
      repeat: Infinity,
      repeatType: "reverse" as const,
      ease: "linear"
    }
  };

  const boopMotion = {
    scale: [1, 1.2, 0.9, 1.1, 1],
    rotate: [0, -10, 10, -5, 0],
    transition: {
      duration: 0.5
    }
  };

  const handleAvatarClick = () => {
    if (isSpeaking) {
      stopSpeaking();
    } else if (streamingResponse) {
      replayResponse(streamingResponse);
    }
    playBeep();
    setIsBooping(true);
    setTimeout(() => setIsBooping(false), 150);
  };

  const handleSubmitThought = async () => {
    if (!thought.trim()) return;
    
    try {
      setError(null);
      setIsSubmitting(true);
      await processThought(thought);
      setThought(''); // Clear input after successful send
      playBeep();
    } catch (err) {
      console.error('Error:', err);
      setError('Quantum field disruption detected. Neural interface temporarily destabilized. Please recalibrate and try again.');
      playError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlayStopClick = () => {
    if (isPlaying) {
      pauseSpeech();
    } else if (isActuallySpeaking) {
      stopSpeaking();
    } else {
      playFromBeginning();
    }
  };

  useEffect(() => {
    if (streamingResponse) {
      setIsWinking(true);
      setTimeout(() => setIsWinking(false), 1000);
    }
  }, [streamingResponse]);

  useEffect(() => {
    if (streamingResponse) {
      setIsResponseComplete(false);
      const glyphMatch = streamingResponse.match(/\[GLYPHS\] (.*?)(?=\n|$)/);
      if (glyphMatch) {
        const glyphs = glyphMatch[1].split('|');
        setCurrentGlyphs(glyphs);
      }
    }
  }, [streamingResponse]);

  useEffect(() => {
    if (neuralActivity !== 'processing' && streamingResponse) {
      const timer = setTimeout(() => {
        setIsResponseComplete(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setIsResponseComplete(false);
    }
  }, [neuralActivity, streamingResponse]);

  return (
    <ThoughtContainer>
      <MainSection>
        <InputSection>
          <NeuralField
            type="text"
            placeholder="Initiate quantum thought transmission..."
            value={thought}
            onChange={(e) => setThought(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isSubmitting) {
                handleSubmitThought();
              }
            }}
            disabled={isSubmitting}
            aria-label="Neural thought input"
          />
          <NeuralActivity activity={neuralActivity} />
        </InputSection>
        
        <AnimatePresence>
          {error && (
            <ErrorMessage
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {error}
            </ErrorMessage>
          )}

          {(streamingResponse || neuralActivity === 'processing') && (
            <ResponseContainer
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <AvatarWrapper>
                <ClickableAvatarWrapper
                  onClick={handleAvatarClick}
                  animate={isBooping ? boopMotion : randomMotion}
                  isSpeaking={isSpeaking}
                >
                  <PulsingRing
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.1, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  />
                  <NeuraAvatar 
                    isSpeaking={isActuallySpeaking && !isMuted}
                  />
                </ClickableAvatarWrapper>
                <SpeechVisualizer 
                  isSpeaking={isActuallySpeaking && !isMuted} 
                  isWinking={isWinking}
                />
              </AvatarWrapper>
              
              <ResponseContent>
                <StreamContainer>
                  {neuralActivity === 'processing' && !streamingResponse ? (
                    <StreamingText>
                      Quantum neural pathways synchronizing...
                      {[
                        "Establishing quantum entanglement...",
                        "Calibrating neural resonance...",
                        "Aligning consciousness matrices...",
                        "Processing quantum thought patterns..."
                      ][Math.floor(Math.random() * 4)]}
                    </StreamingText>
                  ) : (
                    <StreamingResponse text={streamingResponse} />
                  )}
                </StreamContainer>
                {isResponseComplete && <QuantumGlyphs glyphs={currentGlyphs} />}
              </ResponseContent>
            </ResponseContainer>
          )}
        </AnimatePresence>
      </MainSection>
      
      <BottomControls>
        {isSpeaking && (
          <StopButton
            onClick={stopSpeaking}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
          >
            <span>■</span> Terminate Quantum Voice
          </StopButton>
        )}
      </BottomControls>
    </ThoughtContainer>
  );
}; 